// ConfirmActionDialog — modal generico de confirmacao reutilizavel
// (F-UX-PROMOTE-INSTANTIATE-DELETE).
//
// Usado pra confirmar acoes destrutivas/irreversiveis em Skills/Pipelines/Agents:
//   - Promover pra global
//   - Instanciar no tenant
//   - Deletar
//
// Padroes do design system:
//   - Dialog (Radix) ja usado em AgentActions.tsx (PromoteDialog/ForkDialog/InstallDialog)
//   - Variant destructive disponivel em Button pra delete
//   - aria-describedby={undefined} no DialogContent quando description nao usada
//     (Radix exige; padrao usado em AgentActions.tsx)

import { useState, type ReactNode } from 'react';
import { type LucideIcon } from 'lucide-react';
import { Button, type ButtonProps } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';

export interface ConfirmActionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Titulo do modal — ex "Promover skill pra global?" */
  title: string;
  /** Descricao curta abaixo do titulo (opcional). */
  description?: string;
  /** Conteudo livre — ex linha "Skill: <nome>" */
  children?: ReactNode;
  /** Texto do botao primario. Default: "Confirmar". */
  confirmLabel?: string;
  /** Variant do botao primario. Default: brand. Use "destructive" pra delete. */
  confirmVariant?: ButtonProps['variant'];
  /** Icone opcional no botao primario. */
  confirmIcon?: LucideIcon;
  /** Texto do botao secundario. Default: "Cancelar". */
  cancelLabel?: string;
  /**
   * Handler async — devolve Promise. Loading state e tratamento de erro feitos
   * aqui dentro (try/catch + state.error). Modal fecha automaticamente em
   * sucesso (a menos que onConfirm rejeite).
   */
  onConfirm: () => Promise<void>;
}

export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  confirmLabel = 'Confirmar',
  confirmVariant = 'brand',
  confirmIcon: ConfirmIcon,
  cancelLabel = 'Cancelar',
  onConfirm,
}: ConfirmActionDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setError(null);
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="default"
        aria-describedby={description ? undefined : undefined}
      >
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {children && <div className="mt-4 space-y-3">{children}</div>}

        {error && (
          <p role="alert" className="mt-3 text-xs text-raios-danger-text">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            {cancelLabel}
          </Button>
          <Button
            variant={confirmVariant}
            onClick={handleConfirm}
            loading={submitting}
            disabled={submitting}
          >
            {ConfirmIcon && <ConfirmIcon className="w-4 h-4" />}
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
